class VideoSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.video-slider__track');
    this.slides = Array.from(this.querySelectorAll('.video-slider__slide'));
    this.prevBtn = this.querySelector('.video-slider__arrow--prev');
    this.nextBtn = this.querySelector('.video-slider__arrow--next');
    this.dots = Array.from(this.querySelectorAll('.video-slider__dot'));
    this.currentIndex = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
    this.autoplayTimer = null;

    this.init();
  }

  init() {
    if (!this.track || this.slides.length === 0) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.goTo(index);
      });
    });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });

    this.setupMuteToggles();
    this.setupTapToPause();
    this.updateDots();
    this.updateActiveMedia();

    if (this.autoplay) {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }
  }

  setupMuteToggles() {
    this.querySelectorAll('.video-slider__mute-toggle').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const wrapper = button.closest('.video-slider__media-wrapper');
        const video = wrapper ? wrapper.querySelector('video') : null;
        if (!video) return;
        video.muted = !video.muted;
        button.dataset.muted = video.muted ? 'true' : 'false';
      });
    });
  }

  setupTapToPause() {
    this.querySelectorAll('video.video-slider__media').forEach((video) => {
      video.addEventListener('click', () => {
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  toEmbedUrl(url) {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
    if (ytMatch) {
      const id = ytMatch[1];
      return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&rel=0`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1`;
    }
    return url;
  }

  loadEmbed(embed) {
    if (embed.dataset.loaded === 'true') return;
    const url = embed.dataset.embedUrl;
    const src = this.toEmbedUrl(url);
    if (!src) return;
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.className = 'video-slider__iframe';
    embed.innerHTML = '';
    embed.appendChild(iframe);
    embed.dataset.loaded = 'true';
  }

  unloadEmbed(embed) {
    if (embed.dataset.loaded !== 'true') return;
    const iframe = embed.querySelector('iframe');
    if (iframe) iframe.remove();
    embed.dataset.loaded = 'false';
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[clamped];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  onScroll() {
    const trackRect = this.track.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left - trackRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });
    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;
      this.updateDots();
      this.updateActiveMedia();
    }
  }

  updateDots() {
    this.dots.forEach((dot) => {
      const index = parseInt(dot.dataset.index, 10);
      dot.classList.toggle('is-active', index === this.currentIndex);
    });
  }

  updateActiveMedia() {
    this.slides.forEach((slide, i) => {
      const isActive = i === this.currentIndex;
      const video = slide.querySelector('video.video-slider__media');
      const embed = slide.querySelector('.video-slider__embed');

      if (video) {
        if (isActive) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }

      if (embed) {
        if (isActive) {
          this.loadEmbed(embed);
        } else {
          this.unloadEmbed(embed);
        }
      }
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.goTo(nextIndex);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

customElements.define('video-slider-component', VideoSliderComponent);